"use client";

import React from "react";
import { ArrowRight, ShieldCheck, UserCheck, Briefcase } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const sectorList = [{
  name: "Management Consultancies"
}, {
  name: "Commercial Law Firms"
}, {
  name: "Accounting & Tax Advisors"
}, {
  name: "Specialist B2B Agencies"
}, {
  name: "Corporate Advisory"
}];
export default function IndustryHubHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industryhubhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-20 md:py-28 lg:py-32">
      {/* Subtle architectural background gradients (Cobalt blue & blush pink accents) */}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/40 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 -left-20 size-80 rounded-full bg-muted blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mx-auto max-w-4xl text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-6 flex justify-center">
            <Badge variant="outline" className="gap-2 rounded-full border-border bg-card px-4 py-1.5 text-xs font-medium tracking-wide uppercase text-foreground/80 shadow-xs">
              <Briefcase className="size-3.5 text-primary" />
              B2B & Professional Services SEO
            </Badge>
          </motion.div>

          {/* Main H1 Title */}
          <motion.h1 variants={itemVariants} className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            SEO strategy for professional services that need steady, qualified pipeline
          </motion.h1>

          {/* First-person supporting statement */}
          <motion.p variants={itemVariants} className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-xl font-sans">
            I work directly with consultancies, law firms, accountancies, and agencies to build
            search visibility that brings the right corporate buyers to your door. You partner
            directly with me, from first audit to ongoing execution.
          </motion.p>

          {/* Action CTAs */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-sm transition-[background-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Book a Free SEO Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-medium border-border bg-card text-foreground hover:bg-muted/70 rounded-md shadow-xs transition-[background-color,border-color]">
              <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
            </Button>
          </motion.div>

          {/* Trust anchors: 100% direct personal consultant access */}
          <motion.div variants={itemVariants} className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <UserCheck className="size-4 text-primary" />
              <span>You work directly with me</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-primary" />
              <span>Tailored B2B search roadmap</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Sectors Covered Strip */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.6
      }} className="mt-16 sm:mt-20 border-t border-border/80 pt-10">
          <p className="text-center font-serif text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Targeted search strategies tailored for high-value practices
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {sectorList.map((sector, i) => <div key={sector.name} data-index={i} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs sm:text-sm font-medium text-foreground shadow-2xs">
                <span className="size-1.5 rounded-full bg-accent" />
                {sector.name}
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}